/**
 * BundleLanding Component
 *
 * Single-bundle landing page used as the destination for Instagram
 * product tags. Mobile-first, product-as-hero (no long scroll to
 * find the bundle), add-to-cart visible immediately. Designed for
 * paste-into-Instagram-shop URLs:
 *   /essential, /dynamic, /triple-pack
 *
 * Composes existing primitives:
 * - PageLayout (CartProvider, Navigation, Footer, CartDrawer)
 * - ProductCard (full buying UI — flavour, qty, ATC, toast)
 *
 * Each bundle id maps to its static product entry in preloadService.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '../Layout/PageLayout';
import { SEO } from '../SEO';
import { ProductCard } from './ProductCard';
import { STATIC_PRODUCTS } from '../../lib/preloadService';

interface BundleLandingProps {
  bundleId: string;
  seoTitle: string;
  seoDescription: string;
  canonical: string;
  heroTagline: string;
}

export const BundleLanding: React.FC<BundleLandingProps> = ({
  bundleId,
  seoTitle,
  seoDescription,
  canonical,
  heroTagline,
}) => {
  const product = STATIC_PRODUCTS.find((p) => p.id === bundleId);

  if (!product) {
    return (
      <PageLayout>
        <div className="pt-24 pb-16 text-center">
          <p className="text-xl text-white">Bundle not found.</p>
          <Link to="/" className="text-fuchsia-400 underline mt-4 inline-block">
            Back to home
          </Link>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <SEO title={seoTitle} description={seoDescription} canonical={canonical} />

      <div className="pt-20 md:pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4">
          {/* Compact hero — keeps the product above the fold on mobile */}
          <header className="text-center mb-4 md:mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold gradient-text mb-2 md:mb-3">
              {product.name}
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-gray-300">{heroTagline}</p>
          </header>

          {/* The product card — full buying UI, ATC visible after a short scroll */}
          <ProductCard
            id={product.id}
            name={product.name}
            description={product.description}
            price={product.unitPrice}
            imagePlaceholder={product.images[0] || '/placeholder.png'}
            gradientFrom={product.gradients?.from || 'purple'}
            gradientTo={product.gradients?.to || 'fuchsia'}
            buttonFrom="purple"
            buttonTo="fuchsia"
            tier={product.metadata?.tier || 'Basic'}
            freeShipping={product.metadata?.freeShipping || false}
            shippingCost={product.metadata?.shippingCost || 995}
            productFlavor={product.metadata?.flavor || 'Lime Squash'}
            availableFlavors={
              product.metadata?.availableFlavors || ['Lime Squash', 'Orange Crush', 'Pineapple Punch']
            }
            compactMobile
          />

          {/* Trust strip */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 py-4 px-6 rounded-2xl bg-gradient-to-r from-fuchsia-900/20 to-cyan-900/20 border border-fuchsia-500/20">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-fuchsia-400 shrink-0">
              <rect width="18" height="12" x="3" y="8" rx="2" />
              <path d="M7 8V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
            </svg>
            <span className="text-white font-semibold text-base">Free shipping Australia-wide</span>
            <span className="text-gray-500 hidden sm:inline">·</span>
            <span className="text-gray-400 text-sm">Delivered in 2–3 business days</span>
          </div>

          {/* Link back to homepage for full ingredients + FAQ */}
          <div className="mt-8 text-center">
            <Link
              to="/"
              className="inline-block text-fuchsia-400 hover:text-fuchsia-300 transition-colors underline underline-offset-4"
            >
              See full ingredients, FAQs &amp; the whole range →
            </Link>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
